/*
 * Copyright 2026 Sony Corporation
 */
import {
  AgentDownloadResponseSchema,
  ErrorResponseSchema,
  ListChatResponseSchema,
  ListLogsResponseSchema,
  ListRunSummariesResponseSchema,
  ListUsageResponseSchema,
  ListVerificationsResponseSchema,
  UpdateUsageLimitResponseSchema,
  UsageSchema,
  RoadmapSchema,
  RunSchema,
  VerificationSchema,
  VersionSchema,
  VersionDownloadResponseSchema,
  ListDevicesResponseSchema,
  ListVersionsResponseSchema,
  ListProjectsResponseSchema,
  ProjectSchema,
  DeviceSchema,
  DesignPreviewResponseSchema,
  type AgentDownloadResponse,
  type DesignChoice,
  type Roadmap,
  type RoadmapActionRequest,
  type DesignPreviewResponse,
  type CreateDeviceRequest,
  type CreateVersionRequest,
  type Version,
  type VersionDownloadKind,
  type VersionDownloadResponse,
  type ListChatResponse,
  type ListLogsResponse,
  type ListRunSummariesResponse,
  type ListUsageResponse,
  type UpdateUsageLimitResponse,
  type Usage,
  type UpdateDeviceRequest,
  type Verification,
  type VerificationStep,
  type VerificationSummary,
  type UpdateProjectRequest,
  type Run,
  type SendMessageRequest,
  type CreateProjectRequest,
  type Device,
  type Platform,
  type Project,
} from '@ips-jit/schema';
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClient {
  listProjects: () => Promise<Project[]>;
  createProject: (body: CreateProjectRequest) => Promise<Project>;
  getProject: (projectId: string) => Promise<Project>;
  /** Changes a project's settings; today whether device errors are fixed automatically. */
  updateProject: (projectId: string, body: UpdateProjectRequest) => Promise<Project>;
  /** The device log: the latest entries, or those after `cursor` (the one the previous answer gave). */
  listLogs: (projectId: string, options?: LogQuery) => Promise<ListLogsResponse>;
  listDevices: () => Promise<Device[]>;
  createDevice: (body: CreateDeviceRequest) => Promise<Device>;
  deleteDevice: (deviceId: string) => Promise<void>;
  /** Changes a device's settings; today the camera address its apps connect to. */
  updateDevice: (deviceId: string, body: UpdateDeviceRequest) => Promise<Device>;
  /** Issues new credentials and revokes the previous ones; the private key is in this answer and nowhere else. */
  /** Issues new credentials of the runtime for the platform's agent; earlier ones stop working. */
  downloadCredentials: (deviceId: string, os: Platform) => Promise<CredentialsBundle>;
  /** A short-lived link to the agent package (binary and install scripts) of the platform. */
  getAgentDownload: (os: Platform) => Promise<AgentDownloadResponse>;
  listVersions: (projectId: string) => Promise<Version[]>;
  /** Starts a build of the app template with the given files written over it, for delivery to one device. */
  createVersion: (projectId: string, body: CreateVersionRequest) => Promise<Version>;
  /** Sends a finished build, named by its build number, to its device again. */
  deployVersion: (projectId: string, build: number) => Promise<Version>;
  /** A short-lived link to a zip of a build: its binary with a README (once it has one), or its source code. */
  getVersionDownload: (projectId: string, build: number, kind: VersionDownloadKind) => Promise<VersionDownloadResponse>;
  /** Asks the assistant for something; it starts a run that plans the change and waits for approval. */
  sendMessage: (projectId: string, body: SendMessageRequest) => Promise<Run>;
  /** The chat events after `after` (0 for all) and the newest run. */
  listChat: (projectId: string, after: number) => Promise<ListChatResponse>;
  /** Answers the plan a run is waiting on: approving starts the build. */
  /** Answers a plan; with a design option when the user chose one of the drafted ones. */
  decideRun: (projectId: string, runId: string, approved: boolean, design?: DesignChoice) => Promise<Run>;
  /** Pauses, resumes, retries or skips a step of the project's roadmap. */
  roadmapAction: (projectId: string, body: RoadmapActionRequest) => Promise<Roadmap>;
  /** The rendered page of a drafted design option, to show in a sandboxed frame. */
  getDesignPreview: (projectId: string, runId: string, optionId: string) => Promise<DesignPreviewResponse>;
  /** The numbers of every run of the deployment in the last `days` days, for the dashboard. */
  listRunMetrics: (days: number) => Promise<ListRunSummariesResponse>;
  /** The signed-in user's token usage this month against their limit. */
  getUsage: () => Promise<Usage>;
  /** Every user's token usage of a month (this one by default) with their limits; administrators only. */
  listUsage: (period?: string) => Promise<ListUsageResponse>;
  /** Sets a user's monthly token limit; null puts it back to the default. Administrators only. */
  updateUsageLimit: (userId: string, limitTokens: number | null) => Promise<UpdateUsageLimitResponse>;
  /** Asks the device that runs the app to carry out steps in its page; no steps only takes a picture of the screen. */
  createVerification: (projectId: string, steps?: VerificationStep[]) => Promise<Verification>;
  /** The project's recent checks on the device, newest first, without the pictures. */
  listVerifications: (projectId: string) => Promise<VerificationSummary[]>;
  getVerification: (projectId: string, verificationId: string) => Promise<Verification>;
}

export interface LogQuery {
  cursor?: string;
  errorsOnly?: boolean;
  limit?: number;
}

export interface CredentialsBundle {
  blob: Blob;
  filename: string;
}

export interface ApiClientOptions {
  baseUrl: string;
  getIdToken: () => Promise<string | undefined>;
}

/** Every response is parsed with the service's own schema, so a contract change surfaces here. */
const parseWith = async <T extends z.ZodType>(response: Response, schema: T): Promise<z.infer<T>> => {
  const parsed = schema.safeParse(await response.json().catch(() => undefined));
  if (!parsed.success) {
    throw new ApiError(response.status, 'The server returned a response this client does not understand.');
  }
  return parsed.data;
};

export const createApiClient = ({ baseUrl, getIdToken }: ApiClientOptions): ApiClient => {
  const root = baseUrl.replace(/\/+$/, '');

  const request = async (path: string, init: RequestInit = {}): Promise<Response> => {
    const token = await getIdToken();
    if (!token) {
      throw new ApiError(401, 'Your session has expired. Sign in again.');
    }

    const response = await fetch(`${root}${path}`, {
      ...init,
      headers: { ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers, Authorization: token },
    });

    if (!response.ok) {
      // A gateway error page must not be shown to the user verbatim; only the service's own error shape is.
      const body = ErrorResponseSchema.safeParse(await response.json().catch(() => undefined));
      throw new ApiError(response.status, body.success ? body.data.message : response.statusText);
    }
    return response;
  };

  return {
    listProjects: async () => (await parseWith(await request('/projects'), ListProjectsResponseSchema)).projects,
    createProject: async (body) =>
      parseWith(await request('/projects', { method: 'POST', body: JSON.stringify(body) }), ProjectSchema),
    getProject: async (projectId) =>
      parseWith(await request(`/projects/${encodeURIComponent(projectId)}`), ProjectSchema),
    updateProject: async (projectId, body) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
        ProjectSchema
      ),
    listLogs: async (projectId, options = {}) => {
      const query = new URLSearchParams();
      if (options.cursor) {
        query.set('cursor', options.cursor);
      }
      if (options.errorsOnly) {
        query.set('errors', '1');
      }
      if (options.limit) {
        query.set('limit', String(options.limit));
      }
      const suffix = query.size > 0 ? `?${query.toString()}` : '';
      return parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/logs${suffix}`),
        ListLogsResponseSchema
      );
    },
    listDevices: async () => (await parseWith(await request('/devices'), ListDevicesResponseSchema)).devices,
    createDevice: async (body) =>
      parseWith(await request('/devices', { method: 'POST', body: JSON.stringify(body) }), DeviceSchema),
    updateDevice: async (deviceId, body) =>
      parseWith(
        await request(`/devices/${encodeURIComponent(deviceId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
        DeviceSchema
      ),
    deleteDevice: async (deviceId) => {
      await request(`/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' });
    },
    downloadCredentials: async (deviceId, os) => {
      // API Gateway returns the body as bytes only when the request asks for the registered binary type.
      const response = await request(`/devices/${encodeURIComponent(deviceId)}/bundle?os=${os}`, {
        method: 'POST',
        headers: { Accept: 'application/zip' },
      });
      const filename = /filename="?([^";]+)"?/.exec(response.headers.get('Content-Disposition') ?? '')?.[1];
      return { blob: await response.blob(), filename: filename ?? `jit-credentials-${deviceId}.zip` };
    },
    listVersions: async (projectId) =>
      (
        await parseWith(
          await request(`/projects/${encodeURIComponent(projectId)}/versions`),
          ListVersionsResponseSchema
        )
      ).versions,
    createVersion: async (projectId, body) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/versions`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
        VersionSchema
      ),
    deployVersion: async (projectId, build) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/versions/${build}/deploy`, {
          method: 'POST',
        }),
        VersionSchema
      ),
    getVersionDownload: async (projectId, build, kind) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/versions/${build}/download?kind=${kind}`),
        VersionDownloadResponseSchema
      ),
    sendMessage: async (projectId, body) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/messages`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
        RunSchema
      ),
    listChat: async (projectId, after) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/chat?after=${after}`),
        ListChatResponseSchema
      ),
    decideRun: async (projectId, runId, approved, design) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/runs/${encodeURIComponent(runId)}/decision`, {
          method: 'POST',
          body: JSON.stringify(design ? { approved, design } : { approved }),
        }),
        RunSchema
      ),
    roadmapAction: async (projectId, body) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/roadmap`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
        RoadmapSchema
      ),
    getDesignPreview: async (projectId, runId, optionId) =>
      parseWith(
        await request(
          `/projects/${encodeURIComponent(projectId)}/runs/${encodeURIComponent(runId)}/designs/${encodeURIComponent(optionId)}`
        ),
        DesignPreviewResponseSchema
      ),
    getAgentDownload: async (os) =>
      parseWith(await request(`/agent/download?os=${os}&arch=amd64`), AgentDownloadResponseSchema),
    listRunMetrics: async (days) =>
      parseWith(await request(`/metrics/runs?days=${days}`), ListRunSummariesResponseSchema),
    getUsage: async () => parseWith(await request('/usage'), UsageSchema),
    listUsage: async (period) =>
      parseWith(
        await request(`/admin/usage${period ? `?period=${encodeURIComponent(period)}` : ''}`),
        ListUsageResponseSchema
      ),
    updateUsageLimit: async (userId, limitTokens) =>
      parseWith(
        await request(`/admin/usage/${encodeURIComponent(userId)}`, {
          method: 'PUT',
          body: JSON.stringify({ limit_tokens: limitTokens }),
        }),
        UpdateUsageLimitResponseSchema
      ),
    createVerification: async (projectId, steps = []) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/verifications`, {
          method: 'POST',
          body: JSON.stringify({ steps }),
        }),
        VerificationSchema
      ),
    listVerifications: async (projectId) =>
      (
        await parseWith(
          await request(`/projects/${encodeURIComponent(projectId)}/verifications`),
          ListVerificationsResponseSchema
        )
      ).verifications,
    getVerification: async (projectId, verificationId) =>
      parseWith(
        await request(`/projects/${encodeURIComponent(projectId)}/verifications/${encodeURIComponent(verificationId)}`),
        VerificationSchema
      ),
  };
};
